import { useEffect, useRef, useState } from "react";

/**
 * Renders children only after the wrapper scrolls into view (or near it).
 * Saves bandwidth + improves initial load by deferring below-the-fold rails.
 *
 * `placeholderHeight` reserves layout space so the scroll position doesn't jump
 * when the content materializes.
 *
 * v2.97z — Default rootMargin tightened from 1500px → 200px. Earlier "1500px"
 * was effectively eager-mounting every rail on first paint (entire home
 * feed = ~2000px from top), which defeated the lazy purpose. With 200px,
 * a below-fold rail only mounts when the user is ~one rail away from
 * scrolling into it — combined with the new ImdbBatchManager, this slashes
 * initial-paint API traffic from ~33 calls to ~13.
 */
export default function LazyMount({ children, rootMargin = "200px", placeholderHeight = 340, testid }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible || !ref.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(ref.current);
    // v2.32 — Failsafe: if the IntersectionObserver hasn't fired within 6s,
    // force-mount anyway. Some browsers (Safari old, embedded webviews) skip
    // observers when many placeholders stack contiguously — leaving rails
    // permanently invisible. The user reported "Corporate, Ghibli returning
    // no entries"; this guarantees content shows even when IO mis-fires.
    const fallback = setTimeout(() => {
      if (!visible) setVisible(true);
    }, 6000);
    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, [visible, rootMargin]);

  return (
    <div ref={ref} data-testid={testid} style={!visible ? { minHeight: placeholderHeight } : undefined}>
      {visible ? children : null}
    </div>
  );
}
